import React, {
    useState,
    useEffect,
    useRef,
    ChangeEvent,
    useCallback,
    memo,
} from 'react'
import {
    InputAdornment,
    IconButton,
    TextField,
    Grid,
    Tooltip,
} from '@mui/material'
import { translate } from '../i18n'
import { AnyUploaderProps } from './AnyUploader.types'
import { AnyComponentRef } from './AnyComponent.types'
import { AttachIcon, Progress } from '../components'
import { TEXT_REQUIRED_VALIDATOR } from '../validators'

const readImageDimensions = (
    file: File
): Promise<{ width: number; height: number }> =>
    createImageBitmap(file).then((bitmap) => {
        const dimensions = { width: bitmap.width, height: bitmap.height }
        bitmap.close()
        return dimensions
    })

function AnyUploader({
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    fullWidth = true,
    name,
    label,
    initialValue,
    required,
    disabled,
    readOnly,
    acceptExtension,
    tooltip,
    s3Client,
    path,
    register,
    notifier,
    imageDimensions,
}: AnyUploaderProps) {
    const [blured, setBlured] = useState(false)
    const [valid, setValid] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState<string>()
    const [dimensionError, setDimensionError] = useState<string>()
    const [value, setValue] = useState<string>(initialValue ?? '')

    const [uploading, setUploading] = useState(false)
    const fileInput = useRef<HTMLInputElement>(null)

    const ref = useRef<AnyComponentRef<string>>({
        valid,
        dirty,
        value,
        setBlur: () => setBlured(true),
    })

    const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return
        }

        const file = event.target.files[0]
        const newFileName = s3Client.fixKey(path ?? '', file)

        setDirty(true)
        setDimensionError(undefined)

        if (imageDimensions) {
            try {
                const { width, height } = await readImageDimensions(file)
                if (
                    width !== imageDimensions.width ||
                    height !== imageDimensions.height
                ) {
                    setBlured(true)
                    setDimensionError(
                        translate('validations.image_dimensions', {
                            width: imageDimensions.width,
                            height: imageDimensions.height,
                        }) as string
                    )
                    fileInput.current!.value = ''
                    return
                }
            } catch {
                setBlured(true)
                setDimensionError(
                    translate('validations.image_dimensions', {
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                    }) as string
                )
                fileInput.current!.value = ''
                return
            }
        }

        setUploading(true)
        s3Client
            .upload(newFileName, file)
            .then((data: any) => {
                setValue(data ?? newFileName)
            })
            .catch((err: any) => {
                setError(err.message)
            })
            .finally(() => {
                setUploading(false)
                fileInput.current!.value = ''
            })
    }

    const inputBlured = (_event: React.FocusEvent<HTMLInputElement>) => {
        if (!blured) {
            setBlured(true)
        }
    }

    const onNotify = useCallback(
        (value?: string) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    const openFile = () => {
        fileInput.current!.click()
    }

    useEffect(() => {
        if (dimensionError) {
            ref.current.valid = false
            ref.current.dirty = dirty
            ref.current.value = value
            setValid(false)
            setError(dimensionError)
            onNotify(value)
            return
        }

        const valid = !required || TEXT_REQUIRED_VALIDATOR.isValid(value)
        const error = valid ? undefined : TEXT_REQUIRED_VALIDATOR.getMessage()

        ref.current.valid = valid
        ref.current.dirty = dirty
        ref.current.value = value

        setValid(valid)
        setError(error)

        onNotify(value)
    }, [value, dirty, required, onNotify, dimensionError])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    useEffect(() => {
        setValue(initialValue ?? '')
        setDimensionError(undefined)
    }, [initialValue])

    return (
        <Grid
            container
            justifyContent="flex-end"
            alignItems="flex-end"
            spacing={0}
        >
            <Grid item xs={12}>
                <TextField
                    value={value}
                    label={label}
                    variant={variant}
                    margin={margin}
                    size={size}
                    required={required}
                    disabled={disabled || readOnly}
                    error={blured && !valid}
                    helperText={blured ? error : ''}
                    onClick={openFile}
                    fullWidth={fullWidth}
                    onChange={handleUpload}
                    InputProps={{
                        readOnly: readOnly ?? false,
                        endAdornment: readOnly ? undefined : (
                            <InputAdornment position="end">
                                <IconButton
                                    edge="end"
                                    size="medium"
                                    disabled={disabled || uploading}
                                >
                                    {uploading && <Progress />}
                                    {!uploading && tooltip && (
                                        <Tooltip title={tooltip}>
                                            <AttachIcon fontSize="medium" />
                                        </Tooltip>
                                    )}
                                    {!uploading && !tooltip && (
                                        <AttachIcon fontSize="medium" />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <input
                    type="file"
                    disabled={disabled || readOnly}
                    onChange={handleUpload}
                    ref={fileInput}
                    accept={acceptExtension}
                    onBlur={inputBlured}
                    required={required}
                    style={{ display: 'none' }}
                />
            </Grid>
        </Grid>
    )
}

export default memo(AnyUploader)
